import { DragDropProvider, DragOverlay, useDragOperation } from "@dnd-kit/react";
import type { DragEndEvent, DragMoveEvent, DragOverEvent } from "@dnd-kit/react";
import { isSortable } from "@dnd-kit/react/sortable";
import { ReactNode, useRef } from "react";
import { flushSync } from "react-dom";
import { getChecklistEdit, setChecklistEdit } from "./ChecklistEditContext";
import { ELEMENT_ID, initPropertiesElement } from "./constants";
import { captureDragOrigin, getInsertionSlot, insertElementRow, moveElement, moveRow, restoreDragOrigin } from "./dragDrop";
import { ElementPreview } from "./MiddleElement";
import styles from "./style.module.scss";

const isPointerInsideForm = ({ operation }: DragMoveEvent | DragOverEvent | DragEndEvent) => {
    const form = operation.target?.element?.closest('[data-checklist-form]');
    if (!form) return false;
    const { left, right, top, bottom } = form.getBoundingClientRect();
    const { x, y } = operation.position.current;
    return x >= left && x < right && y >= top && y < bottom;
};

type DragDimensions = { width: number, height: number };

const ChecklistDragOverlay = ({ dimensions }: { dimensions: { current: DragDimensions | undefined } }) => {
    const { target } = useDragOperation();
    return <DragOverlay dropAnimation={null} className={target?.data.newRow ? "opacity-30" : undefined}>
        {source => source.type === 'checklist-element'
            ? <div className={`${styles.element} h-full`} data-selected="true" data-vertical-divide={source.data.verticalDivide}
                style={source.data.element.type === ELEMENT_ID.DIVIDE ? dimensions.current : undefined}
            >
                <ElementPreview data={source.data.element} />
            </div>
            : source.type === 'checklist-new-element'
                ? <div className="w-[320px] rounded border-2 border-[var(--colorActive)] bg-[var(--bgSection)] shadow-xl cursor-grabbing">
                    <div className="px-4 py-2 text-xs font-semibold bg-[var(--colorHover)]">
                        + {source.data.name}
                    </div>
                    <div className={styles.element}>
                        <ElementPreview data={initPropertiesElement(source.data.elementType)} />
                    </div>
                </div>
                : <div className="px-4 py-3 bg-[var(--colorHover)] font-semibold rounded cursor-grabbing">
                    Row {source.data.rowIndex + 1}
                </div>}
    </DragOverlay>;
};

const ChecklistDragDrop = ({ children }: { children: ReactNode }) => {
    const get = getChecklistEdit();
    const set = setChecklistEdit();
    const dragOrigin = useRef<ReturnType<typeof captureDragOrigin>>();
    const dragDimensions = useRef<DragDimensions>();

    const updatePalettePreview = (event: DragMoveEvent | DragOverEvent) => {
        const { source, target } = event.operation;
        if (source?.type !== 'checklist-new-element') return;
        const { form, palettePreview } = get();
        const rowIndex = target?.data.rowIndex;
        const position = target?.data.position ?? form[rowIndex]?.length ?? 0;
        if (typeof rowIndex !== 'number' || target?.data.newRow || !isPointerInsideForm(event)) {
            if (palettePreview) set({ palettePreview: undefined });
        } else if (palettePreview?.rowIndex !== rowIndex || palettePreview.position !== position) {
            set({ palettePreview: { rowIndex, position } });
        }
    };

    return <DragDropProvider
        onBeforeDragStart={({ operation: { source } }) => {
            const rect = source?.element?.getBoundingClientRect();
            dragDimensions.current = rect ? { width: rect.width, height: rect.height } : undefined;
            const form = get().getParentEl()?.querySelector<HTMLElement>('[data-checklist-form]');
            const bounds = form?.getBoundingClientRect();
            const anchor = source?.element?.closest<HTMLElement>('[data-checklist-row]')
                ?? (form && bounds ? Array.from(form.querySelectorAll<HTMLElement>('[data-checklist-row]')).find(row => {
                    const rect = row.getBoundingClientRect();
                    return rect.bottom > bounds.top && rect.top < bounds.bottom;
                }) : undefined);
            const top = anchor?.getBoundingClientRect().top;
            flushSync(() => {
                set({ dragging: source?.data.rowIndex ?? -1, palettePreview: undefined, selected: undefined, rowSelected: undefined });
            });
            if (form && anchor && top !== undefined) {
                // Compensate for expanded insertion slots before dnd-kit measures the drag layout.
                form.scrollTop += anchor.getBoundingClientRect().top - top;
            }
        }}
        onDragStart={({ operation: { source } }) => {
            dragOrigin.current = source?.type === 'checklist-new-element' ? undefined : captureDragOrigin(source?.element);
        }}
        onDragMove={updatePalettePreview}
        onDragOver={updatePalettePreview}
        onDragEnd={event => {
            const { source, target } = event.operation;
            const targetRow = target?.data.rowIndex;
            const insertionPosition = target?.data.insertionSlot ? target.data.position : undefined;
            const newRow = target?.data.newRow === true;
            let divideDestination;
            if (source?.type === 'checklist-element' && source.data.element.type === ELEMENT_ID.DIVIDE) {
                const row = target?.element?.closest<HTMLElement>('[data-checklist-row]');
                if (row) {
                    const rowIndex = Number(row.dataset.checklistRow);
                    const elements = Array.from(row.querySelectorAll<HTMLElement>('[data-checklist-position]'))
                        .filter(element => element.dataset.checklistIndex !== String(source.data.element.index))
                        .map(element => {
                            const { left, width } = element.getBoundingClientRect();
                            return { position: Number(element.dataset.checklistPosition), left, width };
                        });
                    divideDestination = {
                        rowIndex,
                        position: getInsertionSlot(elements, event.operation.position.current.x, get().form[rowIndex]?.length ?? 0),
                    };
                }
            }
            const canCreateElement = source?.type === 'checklist-new-element' && isPointerInsideForm(event);
            const sourceRow = source?.data.rowIndex;
            const sourcePosition = source?.data.position;
            const sorted = isSortable(source) ? {
                index: source.index, initialIndex: source.initialIndex, group: source.group,
            } : undefined;
            restoreDragOrigin(dragOrigin.current);
            dragOrigin.current = undefined;
            set({ dragging: undefined, palettePreview: undefined });
            if (event.canceled || !source || !target) return;

            const { form } = get();
            if (typeof targetRow !== 'number') return;

            if (newRow) {
                if (source.type === 'checklist-new-element' && canCreateElement) {
                    set({ form: insertElementRow(form, undefined, targetRow, initPropertiesElement(source.data.elementType)) });
                } else if (source.type === 'checklist-element') {
                    set({ form: insertElementRow(form, { rowIndex: sourceRow, position: sourcePosition }, targetRow) });
                }
                return;
            }

            if (source.type === 'checklist-row') {
                if (sorted) {
                    set({ form: moveRow(form, sorted.initialIndex, sorted.index) });
                }
                return;
            }

            const destination = {
                rowIndex: targetRow,
                position: target.data.position ?? form[targetRow]?.length ?? 0,
            };
            if (source.type === 'checklist-new-element') {
                if (!canCreateElement) return;
                set({ form: moveElement(form, undefined, destination, initPropertiesElement(source.data.elementType)) });
            } else if (source.type === 'checklist-element') {
                if (insertionPosition !== undefined) {
                    destination.position = insertionPosition;
                } else if (sorted && isSortable(target)) {
                    const rowIndex = Number(String(sorted.group).replace('elements-', ''));
                    // Sortable reports the final index, while moveElement accepts an insertion slot.
                    destination.rowIndex = rowIndex;
                    destination.position = sorted.index + (
                        rowIndex === sourceRow && sorted.initialIndex < sorted.index ? 1 : 0
                    );
                }
                if (divideDestination && insertionPosition === undefined) Object.assign(destination, divideDestination);
                set({ form: moveElement(form, {
                    rowIndex: sourceRow,
                    position: sourcePosition,
                }, destination) });
            }
        }}
    >
        {children}
        <ChecklistDragOverlay dimensions={dragDimensions} />
    </DragDropProvider>;
};

export default ChecklistDragDrop;
